<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouterStore } from '@/store/router'
import { useAlojamientos } from '@/composables/useAlojamientos'
import { useNominatim } from '@/composables/useNominatim'
import { usePublicTheme } from '@/composables/usePublicTheme'

usePublicTheme()
const route = useRouterStore()
const token = computed(() => route.params.token)
const { fetchPublicAlojamientoInfo, submitPublicAlojamiento } = useAlojamientos()
const { reverseGeocode } = useNominatim()

const loading = ref(true)
const errorMsg = ref(null)
const congregacionNombre = ref('')

const emptyForm = () => ({ nombre: '', whatsapp: '', ubicacion: '', lat: null, lng: null, cantidad: 1, vehiculo: false })
const form = ref(emptyForm())
const saving = ref(false)
const error = ref(null)
const success = ref(false)

const locating = ref(false)
const locationError = ref(null)

const useMyLocation = () => {
  if (!navigator.geolocation) {
    locationError.value = 'Tu navegador no permite compartir la ubicación. Escribí la dirección manualmente.'
    return
  }
  locating.value = true
  locationError.value = null
  navigator.geolocation.getCurrentPosition(
    async (pos) => {
      form.value.lat = pos.coords.latitude
      form.value.lng = pos.coords.longitude
      try {
        const res = await reverseGeocode({ lat: form.value.lat, lng: form.value.lng })
        if (res?.display_name) form.value.ubicacion = res.display_name
      } catch {
        // Sin conexión al servicio de geocodificación: las coordenadas ya quedaron guardadas igual.
      }
      locating.value = false
    },
    (err) => {
      locating.value = false
      locationError.value = err.code === err.PERMISSION_DENIED
        ? 'No diste permiso para acceder a tu ubicación. Podés escribir la dirección manualmente.'
        : 'No se pudo obtener tu ubicación. Podés escribir la dirección manualmente.'
    },
    { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
  )
}

const load = async () => {
  loading.value = true
  errorMsg.value = null
  if (!token.value) {
    errorMsg.value = 'Enlace inválido: falta el token.'
    loading.value = false
    return
  }
  const res = await fetchPublicAlojamientoInfo(token.value)
  if (res.error) {
    errorMsg.value = 'Este enlace no es válido o fue eliminado.'
  } else {
    congregacionNombre.value = res.congregacion?.name || ''
  }
  loading.value = false
}

const submit = async () => {
  if (!form.value.nombre.trim()) {
    error.value = 'Ingresá un nombre de contacto.'
    return
  }
  saving.value = true
  error.value = null
  const res = await submitPublicAlojamiento(token.value, form.value)
  saving.value = false
  if (res.error) {
    error.value = res.error
  } else {
    success.value = true
  }
}

const cargarOtro = () => {
  form.value = emptyForm()
  success.value = false
  locationError.value = null
}

onMounted(load)
</script>

<template>
  <div class="simple-page">
    <img src="/territorios-icono.png" alt="Logo" class="simple-logo">

    <div v-if="loading" class="simple-loading">
      <i class="pi pi-spin pi-spinner" />
    </div>

    <div v-else-if="errorMsg" class="simple-error">{{ errorMsg }}</div>

    <template v-else>
      <h1 class="simple-title">Alojamientos{{ congregacionNombre ? ` — ${congregacionNombre}` : '' }}</h1>

      <div v-if="success" class="simple-done">
        <i class="pi pi-check-circle" />
        <div>¡Gracias! El alojamiento fue registrado.</div>
        <button class="simple-btn simple-btn-secondary" @click="cargarOtro">Cargar otro alojamiento</button>
      </div>

      <template v-else>
        <div class="simple-hint">
          No hace falta anotar el nombre de cada persona: alcanza con una persona de contacto por casa o alojamiento.
          Si un mismo grupo se reparte en más de un lugar, completá el formulario una vez por cada casa.
        </div>

        <form class="simple-form" @submit.prevent="submit">
          <label class="simple-field-label" for="simple-nombre">Persona de contacto</label>
          <input
            id="simple-nombre"
            v-model="form.nombre"
            type="text"
            class="simple-input"
            placeholder="Nombre y apellido"
          >

          <label class="simple-field-label mt" for="simple-whatsapp">WhatsApp</label>
          <input
            id="simple-whatsapp"
            v-model="form.whatsapp"
            type="tel"
            class="simple-input"
            placeholder="+54 9 11 5555 5555"
          >

          <div class="simple-gps-block mt">
            <button type="button" class="simple-gps-btn" :disabled="locating" @click="useMyLocation">
              <i class="pi" :class="locating ? 'pi-spin pi-spinner' : 'pi-map-marker'" />
              {{ locating ? 'Obteniendo ubicación...' : (form.lat ? 'Actualizar mi ubicación' : 'Usar mi ubicación actual') }}
            </button>
            <div v-if="form.lat && form.lng" class="simple-gps-status">
              <i class="pi pi-check-circle" />
              Ubicación capturada —
              <a :href="`https://www.google.com/maps?q=${form.lat},${form.lng}`" target="_blank" rel="noopener">ver en el mapa</a>
            </div>
            <div v-if="locationError" class="simple-error-msg">{{ locationError }}</div>
          </div>

          <label class="simple-field-label mt" for="simple-ubicacion">Dirección o referencia</label>
          <input
            id="simple-ubicacion"
            v-model="form.ubicacion"
            type="text"
            class="simple-input"
            placeholder="Se completa sola al usar tu ubicación, o escribila a mano"
          >

          <label class="simple-field-label mt" for="simple-cantidad">Cantidad de personas alojadas</label>
          <input
            id="simple-cantidad"
            v-model.number="form.cantidad"
            type="number"
            min="0"
            class="simple-input"
          >

          <label class="simple-checkbox-row mt">
            <input v-model="form.vehiculo" type="checkbox">
            <span>Tenemos vehículo disponible</span>
          </label>

          <div v-if="error" class="simple-error-msg">{{ error }}</div>

          <button type="submit" class="simple-btn" :disabled="saving">
            {{ saving ? 'Enviando...' : 'Registrar alojamiento' }}
          </button>
        </form>
      </template>
    </template>
  </div>
</template>

<style scoped>
.simple-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 20px 48px;
  background: var(--bg-grouped, #f2f2f7);
  text-align: center;
}

.simple-logo {
  width: 112px;
  height: 112px;
  margin-bottom: 28px;
}

.simple-loading {
  font-size: 2rem;
  color: var(--accent, #007aff);
  margin-top: 40px;
}

.simple-error {
  font-size: 18px;
  color: var(--label-secondary, #666);
  margin-top: 20px;
  max-width: 420px;
}

.simple-title {
  font-size: 26px;
  font-weight: 700;
  color: var(--label-primary, #111);
  margin: 0 0 20px;
}

.simple-hint {
  width: 100%;
  max-width: 420px;
  background: var(--info-soft, #e6f2ff);
  color: var(--label-primary, #333);
  border-radius: 12px;
  padding: 14px 16px;
  font-size: 14px;
  line-height: 1.4;
  margin-bottom: 20px;
  text-align: left;
}

.simple-done {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 24px;
  border-radius: 16px;
  background: var(--success-soft, #e3f7e8);
  color: var(--success-deep, #1e7d34);
  font-size: 18px;
  font-weight: 600;
  max-width: 420px;
}

.simple-done .pi {
  font-size: 2.4rem;
}

.simple-form {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 420px;
  text-align: left;
}

.simple-field-label {
  font-size: 15px;
  font-weight: 600;
  color: var(--label-secondary, #666);
  margin-bottom: 8px;
}

.simple-field-label.mt {
  margin-top: 22px;
}

.simple-input {
  width: 100%;
  box-sizing: border-box;
  border-radius: 12px;
  border: 1.5px solid rgba(0, 0, 0, 0.15);
  background: var(--bg-primary, #fff);
  padding: 14px 16px;
  font-size: 17px;
  color: var(--label-primary, #111);
}

.simple-checkbox-row {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  color: var(--label-primary, #111);
  cursor: pointer;
}

.simple-checkbox-row.mt {
  margin-top: 22px;
}

.simple-checkbox-row input {
  width: 20px;
  height: 20px;
}

.simple-error-msg {
  margin-top: 16px;
  color: var(--danger, #ff3b30);
  font-size: 15px;
  text-align: center;
}

.simple-gps-btn {
  width: 100%;
  padding: 16px;
  border: none;
  border-radius: 14px;
  background: var(--accent, #007aff);
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  -webkit-tap-highlight-color: transparent;
}

.simple-gps-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.simple-gps-status {
  margin-top: 10px;
  font-size: 14px;
  color: var(--success-deep, #1e7d34);
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.simple-gps-status a {
  color: inherit;
  text-decoration: underline;
}

.simple-btn {
  margin-top: 28px;
  width: 100%;
  padding: 18px;
  border: none;
  border-radius: 14px;
  background: var(--success, #34c759);
  color: #fff;
  font-size: 19px;
  font-weight: 700;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.simple-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.simple-btn-secondary {
  margin-top: 4px;
  background: rgba(0, 0, 0, 0.12);
  color: var(--label-primary, #111);
}
</style>
